import {child, get, limitToLast, orderByChild, push, query, ref, startAt, update, endAt, equalTo} from "firebase/database";
import {AUTH, dbRef, RDB} from "../../auth/FirebaseContext";
import moment from "moment";
import {getSessionOperator} from "../session";
import {LOG_ACTIONS} from "../../constants";

export async function fetchTenantDataByUid(uid){
    let tin = await (await get(child(dbRef, `/bss/tenants/${uid}/public/legalEntityID`))).val();
    const details =  await getCompanyDetailsByID(tin);
    return {
        ...details,
        TIN: tin
    }
}

export async function fetchClientDataByUid(uid){
    let tin = await (await get(child(dbRef, `/users/${uid}/profile/pub/TIN`))).val();
    let contractID = await (await get(child(dbRef, `/users/${uid}/protected/contractID`))).val();
    const details =  await getCompanyDetailsByID(tin);
    return {
        ...details,
        TIN: tin,
        contractID
    }
}

export async function getCompanyDetailsByID(id){
    return  await (await get(child(dbRef, `/public/businesses/${id}`))).val();
}

export async function isUserHaveEFacture(userUid) {
    let eFacture = await (await get(child(dbRef, `users/${userUid}/protected/efakture`))).val();
    if (!eFacture) {
        return false;
    }
    return eFacture.apiKey;
}

export async function acceptedTermsOfUse(userUid) {
    return await (await get(child(dbRef, `users/${userUid}/private/settings/acceptedLegal`))).val();
}

export async function resetUserTermsOfUse(userUid, index) {
    await update(child(dbRef, `users/${userUid}/private/settings/acceptedLegal/${index}`), {
        accepted: false
    });
}

export async function getLicenseHistory(userUid) {
    return await (await get(child(dbRef, `users/${userUid}/protected/licenseHistory`))).val();
}

export async function getUserActiveLicense(userUid) {
    return await (await get(child(dbRef, `users/${userUid}/protected/license`))).val();
}

export async function insertEFactureLicenseForUser(userUid, data) {
    await addUserLog(userUid, LOG_ACTIONS.eFactureLicense, data);
    await update(child(dbRef, `users/${userUid}/protected/efakture`), {
        apiKey: data.apiKey,
        isProduction: data.isProduction,
        contractTypeID: data.contractTypeID
    });

    let expiredDate = calculateExpiredDate(data.contractTypeID);
    if (expiredDate !== null) {
        await update(child(dbRef, `users/${userUid}/protected/license/2`), {
            count: 1,
            expiredDate: expiredDate,
            productID: 2
        });
    }
}

const calculateExpiredDate = (contractType) => {
    switch (contractType) {
        case "0":
            return null
        default:
            const now = new Date();
            now.setUTCMonth(now.getUTCMonth() + 2);
            now.setUTCDate(1);
            now.setUTCHours(0, 0, 0, 0);
            return now.toISOString();
    }
}

export async function insertFactureApiKeyForUser(userUid, data) {
    await update(child(dbRef, `users/${userUid}/protected/efakture`), data);
    await addUserLog(userUid, LOG_ACTIONS.createApiKey, data);
}

export async function addUserLog(userUid, action, data) {
    const operator = getSessionOperator();
    return push(child(dbRef, `var/log/bss`), {
        operator: operator?.username || null,
        tenant: AUTH.currentUser.uid,
        user: userUid,
        action: action,
        time: new Date().toISOString(),
        data: JSON.stringify(data),
    });
}

export async function fetchLogData(id){
    return await (await get(child(dbRef, `/var/log/bss/${id}/data`))).val();
}

export async function getLogsByUser(uuid){
    const bssRef = ref(RDB, "var/log/bss");
    const firebaseQuery = query(bssRef, orderByChild("user"), equalTo(uuid));
    const snapshot = await get(firebaseQuery);
    const items = snapshot.val();

    if (!items) return [];

    return Object.entries(items).map(([key, value]) => ({
        id: key,
        ...value,
        data: null,
        time: value?.time ? moment.utc(value?.time).format("DD.MM.YYYY HH:mm") : "/"
    }));
}

export async function getLogsByTenant(uuid){
    const bssRef = ref(RDB, "var/log/bss");
    const firebaseQuery = query(bssRef, orderByChild("tenant"), equalTo(uuid));
    const snapshot = await get(firebaseQuery);
    const items = snapshot.val();

    if (!items) return [];

    return Object.entries(items).map(([key, value]) => ({
        id: key,
        ...value,
        data: null,
        time: value?.time ? moment.utc(value?.time).format("DD.MM.YYYY HH:mm") : "/"
    }));
}

export async function getAllLogs(startDate = null, endDate = null, startTime = null, pageSize = 10) {
    const bssRef = ref(RDB, "var/log/bss");

    const queryParams = [orderByChild("time")];
    if (startTime) {
        queryParams.push(endAt(startTime));
    } else if (endDate) {
        queryParams.push(endAt(endDate.toISOString()));
    }

    if (startDate) {
        queryParams.push(startAt(startDate.toISOString()));
    }

    queryParams.push(limitToLast(pageSize + 1));

    const firebaseQuery = query(bssRef, ...queryParams);

    const snapshot = await get(firebaseQuery);
    if (!snapshot.exists()) {
        return { data: [], lastTime: null, hasNextPage: false };
    }

    const items = snapshot.val();

    let entries = Object.entries(items).map(([key, value]) => ({
        id: key,
        ...value,
        time: value?.time,
        formattedTime: value?.time ? moment.utc(value.time).format("DD.MM.YYYY HH:mm") : "/"
    }));
    entries.reverse();

    const hasNextPage = entries.length > pageSize;

    let paginatedData;

    if (startTime) {
        paginatedData = entries.slice(1);
    } else {
        paginatedData = hasNextPage ? entries.slice(0, pageSize) : entries;
    }

    const lastItem = paginatedData.length > 0 ? paginatedData[paginatedData.length - 1] : null;
    const lastTime = (hasNextPage && lastItem) ? lastItem.time : null;

    return {
        data: paginatedData,
        lastTime,
        hasNextPage,
    };
}
